import { useEffect } from 'react'
import { DocumentViewer } from '@/components/ui/document-viewer'
import { usePreviewStore } from '@/stores/preview-store'
import { useResizablePanel } from '@/hooks/useResizablePanel'
import { cn } from '@/lib/utils'

// Default wide enough that a standard portrait page (A4/Letter, ~595–612px at
// 100%) fits without horizontal scroll once the viewer's p-4 padding is added.
const DEFAULT_WIDTH = 820
const MIN_WIDTH = 480
const MAX_WIDTH = 1100
const STORAGE_KEY = 'chat-doc-panel-width'

interface ChatDocumentPanelProps {
  /** Starting width before the user resizes. Defaults to the chat sizing (820). */
  defaultWidth?: number
  /** Narrowest the user can drag it. Defaults to the chat sizing (480). */
  minWidth?: number
  /** Widest the user can drag it. Defaults to the chat sizing (1100). */
  maxWidth?: number
  /**
   * localStorage key the width persists under. Hosts with different width
   * budgets MUST pass their own — a chat-sized 820px persisted width would
   * otherwise blow up a narrower host's layout.
   */
  storageKey?: string
}

/**
 * Docked document reading pane for the chat surfaces (wide screens).
 *
 * Sits to the right of the chat column, swapping documents in place as the
 * user clicks citations. Width is user-adjustable (drag the left edge,
 * double-click to reset) and persisted. Owns Esc-to-close (the modal gets
 * that free from Radix; this pane has no Dialog).
 *
 * Sizing is overridable so non-chat hosts (the Analysis tab, which also has a
 * requirement drawer and a table to fit) can budget the width themselves. The
 * defaults are exactly the chat values, so chat call sites pass nothing.
 */
export function ChatDocumentPanel({
  defaultWidth = DEFAULT_WIDTH,
  minWidth = MIN_WIDTH,
  maxWidth = MAX_WIDTH,
  storageKey = STORAGE_KEY,
}: ChatDocumentPanelProps = {}) {
  const closePreview = usePreviewStore((s) => s.closePreview)
  const { width, isResizing, handleProps } = useResizablePanel({
    defaultWidth,
    minWidth,
    maxWidth,
    storageKey,
    invert: true,
  })

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closePreview()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [closePreview])

  return (
    <div
      className="relative shrink-0 border-l border-border bg-background h-full min-h-0"
      style={{ width }}
    >
      {/* Resize handle on the left edge — drag to resize, double-click to reset */}
      <div
        {...handleProps}
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1.5 -ml-0.5 z-10 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors',
          isResizing && 'bg-primary/30'
        )}
      />
      <DocumentViewer onClose={closePreview} />
    </div>
  )
}
